/**
 * Prestige Home smart search — independent overlay on top of the existing theme.
 * Shopify's original header search remains in the HTML as a no-JS fallback.
 * The modal uses Shopify's own localized predictive-search JSON endpoint.
 */
(function () {
  'use strict';

  // Reuse listeners when Shopify's theme editor reloads the Header section.
  if (window.__PrestigeSearchOverlay && typeof window.__PrestigeSearchOverlay.refresh === 'function') {
    window.__PrestigeSearchOverlay.refresh();
    return;
  }

  var instance = null;
  var listenersAttached = false;
  var ignoreHeaderFocus = false;

  function elementFromEvent(event) {
    return event.target instanceof Element ? event.target : null;
  }

  function findHeaderTrigger(event) {
    var el = elementFromEvent(event);
    return el ? el.closest('#main-header .search-bar__input, #main-header .search-bar__submit, #main-header [data-action="toggle-search"]') : null;
  }

  function safeInternalLink(raw) {
    if (!raw || typeof raw !== 'string') return null;
    try {
      var url = new URL(raw, window.location.href);
      return url.origin === window.location.origin && /^https?:$/.test(url.protocol) ? url.href : null;
    } catch (_) {
      return null;
    }
  }

  function safeImageLink(raw) {
    if (!raw || typeof raw !== 'string') return null;
    try {
      var url = new URL(raw, window.location.href);
      return /^https?:$/.test(url.protocol) ? url.href : null;
    } catch (_) {
      return null;
    }
  }

  function make(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  }

  function money(amount, currency) {
    // Shopify Predictive Search JSON uses decimal currency units (e.g. "3194.10"),
    // unlike Liquid product.price which is represented in minor units.
    var value = Number(amount);
    if (!Number.isFinite(value)) return '';
    if (currency === 'RON') {
      return new Intl.NumberFormat('ro-RO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value) + ' lei';
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'ro-RO', {
        style: 'currency', currency: currency || 'RON'
      }).format(value);
    } catch (_) {
      return new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 2 }).format(value);
    }
  }

  function createProductCard(product, currency) {
    var href = safeInternalLink(product.url);
    if (!href) return null;
    var card = make('a', 'ph-search-product');
    card.href = href;

    var imageWrap = make('span', 'ph-search-product__image');
    var picture = product.featured_image;
    var imgUrl = typeof picture === 'string' ? picture : picture && picture.url;
    if (!imgUrl && product.image) imgUrl = typeof product.image === 'string' ? product.image : product.image.url;
    imgUrl = safeImageLink(imgUrl);
    if (imgUrl) {
      var img = make('img');
      img.src = imgUrl;
      img.alt = (picture && picture.alt) || product.title || '';
      img.loading = 'lazy';
      img.width = 210;
      img.height = 210;
      imageWrap.appendChild(img);
    } else {
      imageWrap.appendChild(make('span', 'ph-search-product__placeholder', 'Prestige'));
    }
    if (product.available === false) imageWrap.appendChild(make('span', 'ph-search-product__badge', 'Indisponibil'));
    card.appendChild(imageWrap);

    card.appendChild(make('span', 'ph-search-product__title', product.title || 'Produs'));
    var priceLine = make('span', 'ph-search-product__prices');
    // Shopify may return a matched color/size variant when the query mentions it.
    // Prefer that variant's price if it has a dedicated link, just as on the product page.
    var matchedVariant = Array.isArray(product.variants) && product.variants.length === 1 ? product.variants[0] : null;
    var variantSelected = matchedVariant && /[?&]variant=/.test(product.url);
    var price = variantSelected && matchedVariant.price != null ? matchedVariant.price :
      (product.price_min !== undefined && product.price_min !== null ? product.price_min : product.price);
    var currentPrice = Number(price);
    if (Number.isFinite(currentPrice)) {
      var varies = !variantSelected && (product.price_varies === true || Number(product.price_max) > currentPrice);
      priceLine.appendChild(make('strong', '', (varies ? 'De la ' : '') + money(currentPrice, currency)));
      var comparePrice = variantSelected ? matchedVariant.compare_at_price :
        (product.compare_at_price_min != null ? product.compare_at_price_min : product.compare_at_price);
      var compare = comparePrice == null ? NaN : Number(comparePrice);
      if (Number.isFinite(compare) && compare > currentPrice) {
        priceLine.appendChild(make('s', '', money(compare, currency)));
      }
    }
    card.appendChild(priceLine);
    return card;
  }

  function setup(dialog) {
    if (instance && instance.dialog === dialog) return;
    if (instance) {
      instance.abort();
      if (instance.dialog.open) instance.dialog.close();
      instance.dialog.remove();
    }
    document.body.appendChild(dialog); // in top layer, outside sticky/transformed header

    var input = dialog.querySelector('#ph-search-input');
    var form = dialog.querySelector('#ph-search-form');
    var discovery = dialog.querySelector('[data-ph-discovery]');
    var loading = dialog.querySelector('[data-ph-loading]');
    var live = dialog.querySelector('[data-ph-live]');
    var footer = dialog.querySelector('[data-ph-footer]');
    var reset = dialog.querySelector('[data-ph-reset]');
    var queryLabel = dialog.querySelector('[data-ph-query]');
    var resultCount = dialog.querySelector('[data-ph-count]');
    var suggestionsSection = dialog.querySelector('[data-ph-suggestions-section]');
    var suggestionsContainer = dialog.querySelector('[data-ph-suggestions]');
    var productsSection = dialog.querySelector('[data-ph-products-section]');
    var productsContainer = dialog.querySelector('[data-ph-products]');
    var productCount = dialog.querySelector('[data-ph-product-count]');
    var collectionsSection = dialog.querySelector('[data-ph-collections-section]');
    var collectionsContainer = dialog.querySelector('[data-ph-collections]');
    var noResults = dialog.querySelector('[data-ph-noresults]');
    var status = dialog.querySelector('[data-ph-status]');
    var currency = dialog.dataset.phCurrency || 'RON';
    var debounceTimer = null;
    var controller = null;
    var requestId = 0;
    var opener = null;

    function abort() {
      clearTimeout(debounceTimer);
      if (controller) controller.abort();
      controller = null;
      requestId += 1;
    }

    function showMode(mode) {
      discovery.hidden = mode !== 'discover';
      loading.hidden = mode !== 'loading';
      live.hidden = mode !== 'results';
      footer.hidden = mode === 'discover';
      input.setAttribute('aria-expanded', mode === 'results' ? 'true' : 'false');
      reset.hidden = !input.value;
    }

    function resetSearch() {
      abort();
      input.value = '';
      status.textContent = '';
      showMode('discover');
      input.focus();
    }

    function search(term, immediate) {
      abort();
      var query = (term || '').trim();
      reset.hidden = !query;
      if (!query) {
        showMode('discover');
        status.textContent = '';
        return;
      }
      queryLabel.textContent = query;
      showMode('loading');
      var thisRequest = requestId;
      debounceTimer = window.setTimeout(function () {
        if (requestId !== thisRequest) return;
        controller = new AbortController();
        var url;
        try {
          url = new URL(dialog.dataset.phSuggestUrl || '/search/suggest.json', window.location.origin);
          url.searchParams.set('q', query);
          url.searchParams.set('resources[type]', 'product,collection,query');
          url.searchParams.set('resources[limit]', '8');
          url.searchParams.set('resources[limit_scope]', 'each');
          url.searchParams.set('resources[options][unavailable_products]', 'last');
          url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,vendor');
        } catch (_) {
          showError();
          return;
        }
        fetch(url.toString(), { credentials: 'same-origin', signal: controller.signal })
          .then(function (response) {
            if (!response.ok) throw new Error('search service unavailable');
            return response.json();
          })
          .then(function (data) {
            if (thisRequest !== requestId || !dialog.open || input.value.trim() !== query) return;
            var results = data && data.resources && data.resources.results;
            if (!results) throw new Error('invalid response');
            renderResults(results, query);
          })
          .catch(function (error) {
            if (error.name === 'AbortError' || thisRequest !== requestId || !dialog.open) return;
            showError();
          });
      }, immediate ? 0 : 220);
    }

    function chip(text) {
      var button = make('button', 'ph-search-chip', '⌕  ' + text);
      button.type = 'button';
      button.dataset.phFill = text;
      return button;
    }

    function renderResults(results, query) {
      var products = Array.isArray(results.products) ? results.products : [];
      var collections = Array.isArray(results.collections) ? results.collections : [];
      var queries = Array.isArray(results.queries) ? results.queries : [];
      var validProducts = 0;
      var validCollections = 0;

      productsContainer.replaceChildren();
      products.forEach(function (product) {
        var card = createProductCard(product, currency);
        if (card) { productsContainer.appendChild(card); validProducts += 1; }
      });
      productsSection.hidden = validProducts === 0;
      productCount.textContent = validProducts + (validProducts === 1 ? ' produs' : ' produse');

      collectionsContainer.replaceChildren();
      collections.forEach(function (collection) {
        var href = safeInternalLink(collection.url);
        if (!href) return;
        var anchor = make('a', 'ph-search-chip ph-search-chip--collection', collection.title || 'Colecție');
        anchor.href = href;
        collectionsContainer.appendChild(anchor);
        validCollections += 1;
      });
      collectionsSection.hidden = validCollections === 0;

      suggestionsContainer.replaceChildren();
      queries.forEach(function (suggestion) {
        var label = suggestion.text || suggestion.query || suggestion.title;
        if (label && typeof label === 'string') suggestionsContainer.appendChild(chip(label));
      });
      suggestionsSection.hidden = suggestionsContainer.childElementCount === 0;

      var total = validProducts + validCollections;
      resultCount.textContent = total + (total === 1 ? ' rezultat' : ' rezultate');
      noResults.hidden = total > 0 || suggestionsContainer.childElementCount > 0;
      noResults.querySelector('h3').textContent = 'N-am găsit rezultate exacte';
      noResults.querySelector('p').textContent = 'Încearcă un termen mai scurt, o categorie sau o altă dimensiune.';
      status.textContent = total ? total + ' rezultate pentru ' + query : 'Niciun rezultat pentru ' + query;
      showMode('results');
    }

    function showError() {
      suggestionsSection.hidden = true;
      productsSection.hidden = true;
      collectionsSection.hidden = true;
      resultCount.textContent = '';
      noResults.hidden = false;
      noResults.querySelector('h3').textContent = 'Căutarea rapidă este indisponibilă';
      noResults.querySelector('p').textContent = 'Poți apăsa Enter pentru a căuta direct în magazin.';
      status.textContent = 'Rezultatele instant nu pot fi încărcate.';
      showMode('results');
    }

    function open(trigger) {
      if (dialog.open) return;
      opener = trigger || document.activeElement;
      // Reset form to a clean discovery screen every time the modal opens.
      input.value = '';
      abort();
      showMode('discover');
      try { dialog.showModal(); } catch (_) { dialog.setAttribute('open', ''); }
      input.focus({ preventScroll: true });
    }

    function close() {
      // Native <dialog> restores focus to the header: that must not reopen it.
      ignoreHeaderFocus = true;
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      window.setTimeout(function () { ignoreHeaderFocus = false; }, 300);
    }

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) close(); // native backdrop click
      var target = elementFromEvent(event);
      if (!target) return;
      if (target.closest('[data-ph-close]')) { close(); return; }
      if (target.closest('[data-ph-reset]')) { resetSearch(); return; }
      if (target.closest('[data-ph-retry]')) { resetSearch(); return; }
      var termButton = target.closest('[data-ph-term], [data-ph-fill]');
      if (termButton) {
        input.value = termButton.dataset.phTerm || termButton.dataset.phFill || '';
        input.focus();
        search(input.value, true);
      }
    });

    dialog.addEventListener('cancel', function () {
      ignoreHeaderFocus = true;
      window.setTimeout(function () { ignoreHeaderFocus = false; }, 300);
    });

    dialog.addEventListener('close', function () {
      abort();
      status.textContent = '';
      // The dialog itself restores the previously focused element for keyboard users.
      opener = null;
    });

    input.addEventListener('input', function () { search(input.value, false); });
    form.addEventListener('submit', function (event) {
      if (!input.value.trim()) {
        event.preventDefault();
        input.focus();
      }
    });

    instance = { dialog: dialog, open: open, close: close, abort: abort };
  }

  function init() {
    var fresh = document.querySelector('#ph-search-dialog:not([data-ph-ready])');
    if (fresh) {
      fresh.dataset.phReady = 'true';
      setup(fresh);
    }
    if (listenersAttached) return;
    listenersAttached = true;

    document.addEventListener('focusin', function (event) {
      if (!instance || ignoreHeaderFocus || instance.dialog.open) return;
      var trigger = findHeaderTrigger(event);
      if (trigger && trigger.matches('input')) {
        event.stopImmediatePropagation(); // prevent theme's legacy inline dropdown
        instance.open(trigger);
      }
    }, true);

    document.addEventListener('click', function (event) {
      if (!instance) return;
      var trigger = findHeaderTrigger(event);
      if (!trigger) return;
      event.preventDefault();
      event.stopImmediatePropagation(); // don't expand the original mobile search
      instance.open(trigger);
    }, true);

    document.addEventListener('submit', function (event) {
      if (!instance) return;
      var form = elementFromEvent(event);
      if (form && form.matches('#main-header form.search-bar')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        instance.open(form.querySelector('.search-bar__input'));
      }
    }, true);

    document.addEventListener('keydown', function (event) {
      if (!instance) return;
      if (instance.dialog.open) {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopImmediatePropagation();
          instance.close();
        }
        return;
      }
      var trigger = findHeaderTrigger(event);
      if (trigger && trigger.matches('input') && event.key === 'Enter') {
        event.preventDefault();
        event.stopImmediatePropagation();
        instance.open(trigger);
      }
    }, true);
  }

  window.__PrestigeSearchOverlay = { refresh: init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
